import {Button, ButtonStyle} from '@mintdata-wsp/common/components/button/Button';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {BindingViewState} from '@mintdata-wsp/common/state/ui/visualDesigner/BindingState';
import {MeshBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshBounds';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {RepeaterFixation} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {EnumUtils} from '@mintdata-wsp/common/util/EnumUtil';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {inject, observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./MeshNamedRangeEditFrame.less');
const cx = classnames.bind(styles);

interface IMeshNamedRangeEditFrameProps
{
    ctx?:IWSPMeshContext;
}

@inject('ctx')
@observer
export class MeshNamedRangeEditFrame extends BaseComponentWithContext<IMeshNamedRangeEditFrameProps>
{
    private _popupId = IdUtil.newGUID();

    public render():JSX.Element
    {
        const {sheetId} = this.props.ctx;

        const bindingState = this._getBindingState();
        const {bindingPhase, renderingSheetId} = bindingState;

        if( !bindingPhase || sheetId !== renderingSheetId )
            return null;

        const meshState = this._getMeshState();

        const {isEditing, isArray, normalizedSelectionBounds} = bindingState;
        const {isDown, startLoc, endLoc} = normalizedSelectionBounds;

        const popupRef = this.context.popupManager
            .position(this._popupId, SourceAnchor.LeftTop, isDown ? PopupAnchor.LeftTop : PopupAnchor.RightTop);

        const renderExtendControls = isEditing && isArray;
        const frameBounds = MeshUtils.getRangeBounds(startLoc, endLoc, meshState);

        return <div
            style={this._getFrameStyle(frameBounds)}
            className={styles.base}
            ref={popupRef}
        >
            {this._renderFloatingControls(isDown)}
            {renderExtendControls && this._renderExtendHandler(isDown)}
            {renderExtendControls && this._renderExtendButtons(isDown)}
        </div>;
    }

    private _renderFloatingControls(isDown:boolean):JSX.Element
    {
        const {ctx} = this.props;
        const {mesh} = controllers.ui.spreadsheets;

        const {isEditing, normalizedSelectionBounds} = this._getBindingState();
        const {startLoc} = normalizedSelectionBounds;

        if( isDown )
        {
            return <Popover id={this._popupId} ignoreEsc={true}>
                <div className={styles.floatingControlsWrapper}>
                    {this._renderLabels(isDown)}
                    {this._renderFixationButton()}
                    {this._renderFormatButton()}
                </div>
            </Popover>;
        }
        else
        {
            const cellSize = mesh.getCellSize(ctx, startLoc);
            const cellWidth = (cellSize) ? cellSize.width - 4 : 100;

            return <Popover id={this._popupId} ignoreEsc={true} className={styles.floatingControlsPopover}>
                <ul style={{width: `${cellWidth}px`}} className={styles.floatingControlsWrapperOnLeftSide}>
                    {this._renderLabels(isDown)}
                    {this._renderFixationButton()}
                    {this._renderFormatButton()}
                </ul>
            </Popover>;
        }
    }

    private _renderFixationButton():JSX.Element
    {
        const {isEditing, repeaterFixation} = this._getBindingState();
        if( !isEditing )
            return null;

        const fixationBtnCaption = EnumUtils.fixationCaption(repeaterFixation);
        return <Button
            className={styles.fixationBtn}
            onClick={this._onChangeFixationClick}
            onMouseDown={this._stopEventPropagation}
            onDblClick={this._stopEventPropagation}
            caption={fixationBtnCaption}
            icon={MDIcons.common.Lock}
            style={ButtonStyle.Raised}
        />;
    }

    private _renderFormatButton():JSX.Element
    {
        const {mayFormatBeApplied, useFormat, isEditing} = this._getBindingState();
        if( !mayFormatBeApplied || !isEditing )
            return null;

        const formatBtnCaption = useFormat ? 'Format Bound' : 'Format Unbound';
        return <Button
            className={styles.useFormatBtn}
            onClick={this._onToggleUseFormatClick}
            onMouseDown={this._stopEventPropagation}
            onDblClick={this._stopEventPropagation}
            caption={formatBtnCaption}
            style={ButtonStyle.Raised}
        />;
    }

    private _renderLabels(isDown:boolean):Array<JSX.Element>
    {
        const {ctx} = this.props;
        const {mesh} = controllers.ui.spreadsheets;
        const {propLabels, normalizedSelectionBounds} = this._getBindingState();

        let {rowIndex, colIndex} = normalizedSelectionBounds.startLoc;

        return propLabels.map((propLabel, propIndex) =>
        {
            const labelCellLocation = isDown
                ? new MeshLocation(colIndex + propIndex, rowIndex - 1)
                : new MeshLocation(colIndex, rowIndex + propIndex);

            const cellSize = mesh.getCellSize(ctx, labelCellLocation);

            if( isDown )
            {
                const cellWidth = cellSize.width - (colIndex === 0 ? 1 : 0) - (colIndex === propLabels.length ? 1 : 0);
                return (<div
                    key={propIndex}
                    className={styles.propLabel}
                    style={{
                        width: `${cellWidth}px`
                    }}
                >
                    {propLabel}
                </div>);
            }
            else
            {
                return (<li
                    key={propIndex}
                    className={styles.propLabelOnLeftSide}
                    style={{height: `${cellSize.height}px`}}
                >
                    {propLabel}
                </li>);
            }
        });
    }

    private _renderExtendHandler(isDown:boolean):JSX.Element
    {
        return (
            <div className={cx('extendHandlerWrapper', {down: isDown, right: !isDown})}>
                <div
                    className={styles.extendHandler}
                    onMouseDown={this._onExtendHandlerMouseDown}
                />
            </div>
        );
    }

    private _renderExtendButtons(isDown:boolean):JSX.Element
    {
        const {editNamedRange} = this._getBindingState();

        const showAutoBtn = !editNamedRange.isAuto();

        return (
            <div className={cx('extendButtonsWrapper', {down: isDown, right: !isDown})}>
                <div className={cx('extendButtonsInnerWrapper', {down: isDown, right: !isDown})}>
                    <Button
                        className={styles.changeDirectionBtn}
                        onClick={this._onChangeDirectionClick}
                        onMouseDown={this._stopEventPropagation}
                        onDblClick={this._stopEventPropagation}
                        caption=""
                        icon={isDown ? MDIcons.common.ArrowDropright : MDIcons.common.ArrowDropdown}
                        style={ButtonStyle.Raised}
                    />
                    {showAutoBtn && <Button
                        className={styles.toggleAutoBtn}
                        onClick={this._onToggleAutoClick}
                        onMouseDown={this._stopEventPropagation}
                        onDblClick={this._stopEventPropagation}
                        caption="Auto"
                        icon={isDown ? MDIcons.common.ArrowDropdown : MDIcons.common.ArrowDropright}
                        style={ButtonStyle.Raised}
                    />}
                </div>
            </div>
        );
    }

    private _getBindingState():BindingViewState
    {
        return controllers.ui.bindings.getViewState();
    }

    private _getMeshState():MeshState
    {
        return controllers.ui.spreadsheets.mesh.getState(this.props.ctx);
    }

    private _getFrameStyle(bounds:MeshBounds):React.CSSProperties
    {
        return {
            transform: `translate(${bounds.x}px, ${bounds.y}px)`,
            width: `${bounds.width}px`,
            height: `${bounds.height}px`
        };
    }

    @bind
    private _onExtendHandlerMouseDown(event:React.MouseEvent<HTMLDivElement>):void
    {
        const {ctx} = this.props;
        controllers.ui.bindings.startPropBindingExtension(ctx);
        event.stopPropagation();
    }

    @bind
    private _onToggleUseFormatClick(event:React.MouseEvent<HTMLButtonElement>):void
    {
        const {ctx} = this.props;
        controllers.ui.bindings.togglePropBindingUseFormat(ctx);
        event.stopPropagation();
    }

    @bind
    private _onChangeFixationClick(event:React.MouseEvent<HTMLButtonElement>):void
    {
        const {ctx} = this.props;
        const {repeaterFixation} = this._getBindingState();

        const newFixation = repeaterFixation === RepeaterFixation.FixedBoth
            ? RepeaterFixation.FixedDown
            : repeaterFixation === RepeaterFixation.FixedDown
                ? RepeaterFixation.FixedRight
                : repeaterFixation === RepeaterFixation.FixedRight
                    ? RepeaterFixation.NotFixed
                    : RepeaterFixation.FixedBoth;

        controllers.ui.bindings.editBindingFixation(ctx, newFixation);
        event.stopPropagation();
    }

    @bind
    private _onChangeDirectionClick(event:React.MouseEvent<HTMLButtonElement>):void
    {
        const {ctx} = this.props;
        controllers.ui.bindings.togglePropBindingDirection(ctx);
        event.stopPropagation();
    }

    @bind
    private _onToggleAutoClick(event:React.MouseEvent<HTMLButtonElement>):void
    {
        const {ctx} = this.props;
        controllers.ui.bindings.togglePropBindingAutoMode(ctx);
        event.stopPropagation();
    }

    @bind
    private _stopEventPropagation(event:React.MouseEvent<HTMLButtonElement>):void
    {
        event.stopPropagation();
    }
}
